import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Zap, Crown, ChevronRight, Shield, Clock, Headphones, AlertCircle, X, CheckSquare, Square, Gift, Info, ExternalLink, MessageCircle, Tag, Percent, Key, Infinity, Cpu, TrendingUp, Star } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../AuthContext';
import { useLanguage } from '../LanguageContext';
import { useSiteStats } from '../SiteStatsContext';
import { showDiscordRequired } from './DiscordRequiredModal';
import WindowsCard from './services/WindowsCard';
import PackCard from './services/PackCard';
import { LicenceUsesCard } from './services/LicenceUsesCard';
import { BiosCard } from './services/BiosCard';
import './services/services.css';

const LICENCE_TIERS = [
  { id: 'licence_1',        uses: 1,    label: '1 utilisation',    price: 14.99, popular: false, lifetime: false },
  { id: 'licence_2',        uses: 2,    label: '2 utilisations',   price: 27.99, popular: false, lifetime: false },
  { id: 'licence_3',        uses: 3,    label: '3 utilisations',   price: 39.99, popular: true,  lifetime: false },
  { id: 'licence_4',        uses: 4,    label: '4 utilisations',   price: 49.99, popular: false, lifetime: false },
  { id: 'licence_5',        uses: 5,    label: '5 utilisations',   price: 59.99, popular: false, lifetime: false },
  { id: 'licence_lifetime', uses: 999,  label: 'Lifetime',         price: 79.99, popular: false, lifetime: true  },
];

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ServicesPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loginWithDiscord, createOrder, token, user } = useAuth();
  const { t, language } = useLanguage();
  const stats = useSiteStats();
  const [loading, setLoading] = useState(null);
  const [success, setSuccess] = useState(null);
  const [hasActiveOrder, setHasActiveOrder] = useState(false);
  const [orderError, setOrderError] = useState(null);
  const [altPaymentLoading, setAltPaymentLoading] = useState(null);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [isPriorityChecked, setIsPriorityChecked] = useState(false);
  const [isPreparationChecked, setIsPreparationChecked] = useState(false);
  const [biosCompatibilityChecked, setBiosCompatibilityChecked] = useState(false);
  const [isIntelChecked, setIsIntelChecked] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [srvPrices, setSrvPrices] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/pricing`).then(r => r.json()).then(d => setSrvPrices(d.prices)).catch(() => {});
  }, []);

  const licenceTiers = LICENCE_TIERS.map(t => ({ ...t, price: srvPrices?.[t.id] ?? t.price }));
  
  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [promoError, setPromoError] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoValidated, setPromoValidated] = useState(false);
  // Affiliate promo auto-applied via affiliate link
  const [affiliatePromo, setAffiliatePromo] = useState(null);
  const [promoAuto, setPromoAuto] = useState(false);
  // Réduction PARRAINAGE (liée au compte, appliquée auto sur les optimisations)
  const [referralDiscount, setReferralDiscount] = useState(0);

  // Charge la réduction de parrainage de l'utilisateur connecté
  useEffect(() => {
    if (!isAuthenticated || !token) { setReferralDiscount(0); return; }
    fetch(`${API_URL}/api/referral/invite-stats`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d) setReferralDiscount(d.discount_percent || 0); })
      .catch(() => {});
  }, [isAuthenticated, token]);

  // Load the affiliate's promo code (if any) from the stored affiliate link → auto-apply its discount
  useEffect(() => {
    const affCode = localStorage.getItem('affiliate_code');
    if (!affCode) return;
    fetch(`${API_URL}/api/affiliates/${affCode}/promo`)
      .then(r => r.json())
      .then(d => { if (d && d.has_promo && d.promo_code) setAffiliatePromo(d); })
      .catch(() => {});
  }, []);

  // Auto-apply the affiliate promo for the currently selected service (respects service restrictions).
  useEffect(() => {
    if (!affiliatePromo || !affiliatePromo.promo_code) return;
    if (!selectedService) return;
    // Don't override a DIFFERENT code the user typed & validated manually
    if (promoValidated && !promoAuto && promoCode &&
        promoCode.toUpperCase() !== affiliatePromo.promo_code.toUpperCase()) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${API_URL}/api/promo-codes/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: affiliatePromo.promo_code, service_type: selectedService }),
        });
        if (cancelled) return;
        if (resp.ok) {
          const data = await resp.json();
          setPromoCode(affiliatePromo.promo_code);
          setPromoDiscount(data.discount_percent);
          setPromoValidated(true);
          setPromoAuto(true);
          setPromoError('');
          setShowCodes(true);
        } else if (promoAuto) {
          // The affiliate promo isn't valid for this service → clear it
          setPromoValidated(false);
          setPromoDiscount(0);
          setPromoAuto(false);
          setPromoCode('');
        }
      } catch (e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [affiliatePromo, selectedService]);

  // Flash sale state
  const [flashSale, setFlashSale] = useState(null);
  
  // PayPal success popup state
  const [showPaypalSuccess, setShowPaypalSuccess] = useState(false);
  const [paypalTicketData, setPaypalTicketData] = useState(null);

  // Conflict popup state (already has active ticket/order)
  const [conflict, setConflict] = useState(null); // { type, message, discord_url, serviceName }

  // Collapsible "I have a code" section
  const [showCodes, setShowCodes] = useState(false);
  
  // Category tab
  const [activeCategory, setActiveCategory] = useState('optimisation');
  const [licenceLoading, setLicenceLoading] = useState(null);
  const [hasLicence, setHasLicence] = useState(false);

  // A user is "client" (existing customer) if they have the Discord Client role (backend-authoritative)
  const isClient = (user && user.is_client === true);

  // ===== Client discount config (fetched from /api/client-discount) =====
  const [clientDiscount, setClientDiscount] = useState({ enabled: false, rules: {} });
  useEffect(() => {
    fetch(`${API_URL}/api/client-discount`)
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setClientDiscount(d))
      .catch(() => {});
  }, []);

  // Helper: compute client discounted price (for users with Client role)
  const getClientDiscountedPrice = (basePrice, category) => {
    if (!isClient || !clientDiscount.enabled) return { final: basePrice, discounted: false, label: null, original: basePrice };
    const rule = clientDiscount.rules?.[category];
    if (!rule || !rule.enabled || !rule.value) return { final: basePrice, discounted: false, label: null, original: basePrice };
    const final = rule.type === 'percent'
      ? basePrice * (1 - rule.value / 100)
      : Math.max(0, basePrice - rule.value);
    const label = rule.type === 'percent' ? `-${parseInt(rule.value)}%` : `-${rule.value.toFixed(2).replace('.', ',')}€`;
    return { final: Math.round(final * 100) / 100, discounted: true, label, original: basePrice };
  };

  // Pre-compute discount for each service (re-runs when isClient/discount changes)
  const windowsDisc = getClientDiscountedPrice(srvPrices?.windows ?? 19, 'windows');
  // Pack Complet: prix client fixe à 29€ (remplace le prix normal), sinon prix catalogue
  const CLIENT_PACK_PRICE = 29;
  const packStandardBaseRaw = srvPrices?.pack ?? 32;
  const packEffectiveBase = isClient ? CLIENT_PACK_PRICE : packStandardBaseRaw;
  const packDisc = {
    final: packEffectiveBase,
    discounted: isClient,
    label: isClient ? 'Client' : null,
    original: packStandardBaseRaw,
  };
  const biosDisc = getClientDiscountedPrice(srvPrices?.bios ?? 13.99, 'bios');

  // Licence tiers shown on the "Acheter des utilisations" card (with client discount)
  const usesCardTiers = [
    { id: 'licence_1', label: '1 utilisation', star: false },
    { id: 'licence_3', label: '3 utilisations', star: true },
    { id: 'licence_5', label: '5 utilisations', star: false },
    { id: 'licence_lifetime', label: 'Lifetime', star: false },
  ].map((t) => {
    const base = srvPrices?.[t.id] ?? LICENCE_TIERS.find((lt) => lt.id === t.id)?.price ?? 0;
    const d = getClientDiscountedPrice(base, t.id);
    return { ...t, final: d.final, discounted: d.discounted, original: d.original, discLabel: d.label };
  });

  // Check if user has a licence (unlocks the Licences tab)
  useEffect(() => {
    if (!isAuthenticated || !token) { setHasLicence(false); return; }
    fetch(`${API_URL}/api/user/licence-status`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(d => setHasLicence(d.has_licence === true))
      .catch(() => setHasLicence(false));
  }, [isAuthenticated, token]);

  // ── Licence order handler ──────────────────────────────────────────────
  const handleLicenceOrder = async (tierId, method = 'stripe') => {
    if (!isAuthenticated) { loginWithDiscord(); return; }
    setLicenceLoading(`${tierId}_${method}`);
    try {
      if (method === 'stripe') {
        const res = await fetch(`${API_URL}/api/create-checkout-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ service_type: tierId, affiliate_code: '', promo_code: '' }),
        });
        const data = await res.json();
        if (res.status === 403 && (data.detail === 'not_in_discord' || data.detail === 'discord_banned')) {
          setLicenceLoading(null);
          showDiscordRequired(data.detail);
          return;
        }
        if (data.url) window.location.href = data.url;
      } else {
        // PayPal or PSC — reuse existing alt payment flow
        const tier = licenceTiers.find(t => t.id === tierId);
        const res = await fetch(`${API_URL}/api/create-payment-ticket`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            payment_method: method,
            service_type: tierId,
            service_name: tier?.label ? `Licence AqmOpti — ${tier.label}` : tierId,
            total_price: tier?.price || 0,
            options: {},
            affiliate_code: '',
            promo_code: '',
            original_price: tier?.price || 0,
            discount_percent: 0,
          }),
        });
        const data = await res.json();
        if (res.status === 403 && (data.detail === 'not_in_discord' || data.detail === 'discord_banned')) {
          setLicenceLoading(null);
          showDiscordRequired(data.detail);
          return;
        }
        if (res.ok) {
          setPaypalTicketData({
            method,
            serviceName: tier?.label ? `Licence AqmOpti — ${tier.label}` : tierId,
            totalPrice: method === 'paysafecard' ? (tier?.price || 0) + 5 : (tier?.price || 0),
            discord_url: data.discord_url,
          });
          setShowPaypalSuccess(true);
        }
      }
    } catch (e) {
      console.error('Licence order error:', e);
    } finally {
      setLicenceLoading(null);
    }
  };

  // Fetch flash sale
  useEffect(() => {
    const fetchFlashSale = async () => {
      try {
        const response = await fetch(`${API_URL}/api/flash-sale`);
        if (response.ok) {
          const data = await response.json();
          if (data.active) {
            setFlashSale(data);
          }
        }
      } catch (error) {
        console.error('Error fetching flash sale:', error);
      }
    };
    fetchFlashSale();
  }, []);

  // Check if flash sale applies to a service
  const getFlashSaleDiscount = (serviceType) => {
    if (!flashSale || !flashSale.active) return 0;
    // If services is null or empty, applies to all
    if (!flashSale.services || flashSale.services.length === 0) {
      return flashSale.discount_percent;
    }
    // Check if this service is in the list
    if (flashSale.services.includes(serviceType)) {
      return flashSale.discount_percent;
    }
    return 0;
  };

  // Affiliate promo discount for a given service (0 if the affiliate has no promo or it doesn't cover this service)
  const affiliatePromoDiscountFor = (serviceType) => {
    if (!affiliatePromo || !affiliatePromo.promo_code || !affiliatePromo.discount_percent) return 0;
    const services = affiliatePromo.services;
    if (!services || services.length === 0) return affiliatePromo.discount_percent;
    const n = (serviceType || '').toLowerCase().replace(/-/g, '_');
    let base = n;
    if (n.startsWith('licence')) base = 'licence';
    else if (n.startsWith('pack')) base = 'pack';
    else if (n.startsWith('windows')) base = 'windows';
    else if (n.startsWith('bios')) base = 'bios';
    else if (n.startsWith('aqmoptitool')) base = 'aqmoptitool';
    return services.includes(base) ? affiliatePromo.discount_percent : 0;
  };

  // Réduction parrainage applicable à un service (optimisations uniquement)
  const referralDiscountFor = (serviceType) => {
    if (!referralDiscount) return 0;
    const n = (serviceType || '').toLowerCase().replace(/-/g, '_');
    const isOpti = n.startsWith('windows') || n.startsWith('pack') || n.startsWith('bios');
    return isOpti ? referralDiscount : 0;
  };

  // Combined discount shown on service cards = best of flash sale, affiliate promo and referral
  const getCardDiscount = (serviceType) => Math.max(
    getFlashSaleDiscount(serviceType),
    affiliatePromoDiscountFor(serviceType),
    referralDiscountFor(serviceType),
  );

  // Vérifier si l'utilisateur a déjà une commande
  useEffect(() => {
    const checkExistingOrder = async () => {
      if (!isAuthenticated || !token) return;
      
      try {
        const response = await fetch(`${API_URL}/api/orders`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const orders = await response.json();
          const activeOrder = orders.find(o => o.status !== 'completed' && o.status !== 'cancelled');
          setHasActiveOrder(!!activeOrder);
        }
      } catch (error) {
        console.error('Error checking orders:', error);
      }
    };

    checkExistingOrder();
  }, [isAuthenticated, token]);

  const openOrderModal = (serviceType) => {
    if (!isAuthenticated) {
      loginWithDiscord();
      return;
    }

    if (hasActiveOrder) {
      setOrderError('Tu as déjà une commande en cours ! Consulte ton ticket Discord.');
      setTimeout(() => setOrderError(null), 5000);
      return;
    }

    setSelectedService(serviceType);
    setIsPriorityChecked(false);
    setIsPreparationChecked(false);
    setBiosCompatibilityChecked(false);
    setIsIntelChecked(false);
    setShowCodes(false);
    setReferralCode('');
    setPromoCode('');
    setPromoDiscount(0);
    setPromoError('');
    setPromoValidated(false);
    setShowModal(true);
  };

  // Validate promo code
  const validatePromoCode = async () => {
    if (!promoCode.trim()) return;
    
    setPromoLoading(true);
    setPromoError('');
    
    try {
      const response = await fetch(`${API_URL}/api/promo-codes/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          code: promoCode.toUpperCase(),
          service_type: selectedService  // Send service to check if code applies
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setPromoDiscount(data.discount_percent);
        setPromoValidated(true);
        setPromoError('');
      } else {
        const error = await response.json();
        // Afficher des messages d'erreur plus clairs
        let errorMessage = error.detail || 'Code promo invalide';
        if (errorMessage.includes('invalide')) {
          errorMessage = 'Code promo invalide';
        } else if (errorMessage.includes('expiré')) {
          errorMessage = 'Code promo expiré';
        } else if (errorMessage.includes('épuisé')) {
          errorMessage = 'Code promo épuisé';
        } else if (errorMessage.includes('désactivé')) {
          errorMessage = 'Code promo désactivé';
        }
        setPromoError(errorMessage);
        setPromoDiscount(0);
        setPromoValidated(false);
      }
    } catch (error) {
      setPromoError('Code promo invalide');
      setPromoDiscount(0);
      setPromoValidated(false);
    } finally {
      setPromoLoading(false);
    }
  };

  const handleOrder = async () => {
    // Block BIOS-related orders without compatibility confirmation
    if (requiresBiosWarning() && !biosCompatibilityChecked) {
      setOrderError("Tu dois confirmer que ton PC est compatible avec l'optimisation BIOS avant de commander.");
      return;
    }

    setLoading(selectedService);
    setSuccess(null);
    setOrderError(null);

    try {
      // Apply referral code if provided and user hasn't used one yet
      if (referralCode.trim() && !user?.referred_by) {
        try {
          const refResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/referral/use?referral_code=${referralCode.toUpperCase()}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (!refResponse.ok) {
            const error = await refResponse.json();
            setOrderError(error.detail || 'Code de parrainage invalide');
            setLoading(null);
            return; // Don't close modal, let user fix the code
          }
        } catch (refError) {
          console.error('Referral error:', refError);
          // Continue without referral code if there's an error
        }
      }

      // Prepare order type with options
      let orderType = selectedService;
      const options = [];
      if (isPriorityChecked) options.push('prioritaire');
      if (isPreparationChecked) options.push('preparation');
      if (isIntelChecked && (selectedService === 'pack' || selectedService === 'bios')) options.push('intel');
      if (options.length > 0) {
        orderType = `${selectedService}-${options.join('-')}`;
      }
      
      // Get affiliate code from localStorage if exists
      const affiliateCode = localStorage.getItem('affiliate_code');
      
      console.log('🔥 STRIPE CHECKOUT - Creating session for:', orderType, 'affiliate:', affiliateCode, 'promo:', promoValidated ? promoCode : null);
      
      // Create Stripe Checkout Session
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          service_type: orderType,
          affiliate_code: affiliateCode,
          promo_code: promoValidated ? promoCode.toUpperCase() : null
        })
      });

      if (!response.ok) {
        let error = {};
        try {
          error = await response.json();
        } catch (_) {
          error = { detail: 'Erreur lors de la création de la session de paiement' };
        }
        if (response.status === 403 && (error.detail === 'not_in_discord' || error.detail === 'discord_banned')) {
          setShowModal(false);
          setLoading(null);
          showDiscordRequired(error.detail);
          return;
        }
        if (response.status === 409) {
          const detail = error.detail || '';
          const urlMatch = detail.match(/https?:\/\/[^\s]+/);
          setShowModal(false);
          setConflict({
            type: detail.toLowerCase().includes('ticket') ? 'ticket' : 'order',
            method: 'stripe',
            message: detail,
            discord_url: urlMatch ? urlMatch[0] : null,
            serviceName: selectedService === 'windows' ? 'Windows' : selectedService === 'bios' ? 'BIOS' : 'Pack Complet',
          });
          setLoading(null);
          return;
        }
        throw new Error(error.detail || 'Erreur lors de la création de la session de paiement');
      }

      let payload;
      try {
        payload = await response.json();
      } catch (_) {
        throw new Error('Réponse invalide du serveur');
      }
      const { url } = payload;
      
      // Redirect to Stripe Checkout (loading will stay until redirect completes)
      window.location.href = url;
      
    } catch (err) {
      console.error('Error:', err);
      setOrderError('Une erreur est survenue. Réessayez plus tard.');
      setLoading(null);
      // Keep modal open so user can retry
    }
  };

  // Helper: services that require BIOS compatibility confirmation
  const requiresBiosWarning = () => {
    const s = (selectedService || '').toLowerCase();
    return s.includes('bios') || s.includes('pack');
  };

  // Handle alternative payment methods (PayPal, Paysafecard)
  const handleAlternativePayment = async (method) => {
    if (!isAuthenticated) {
      loginWithDiscord();
      return;
    }

    if (requiresBiosWarning() && !biosCompatibilityChecked) {
      setOrderError("Tu dois confirmer que ton PC est compatible avec l'optimisation BIOS avant de commander.");
      return;
    }

    setAltPaymentLoading(method);

    try {
      // Build service description
      const options = [];
      if (isPriorityChecked) options.push('Prioritaire');
      if (isPreparationChecked) options.push('Préparation');
      if (isIntelChecked && (selectedService === 'pack' || selectedService === 'bios')) options.push('Intel');
      
      const serviceName = selectedService === 'windows' ? 'Windows' : selectedService === 'bios' ? 'BIOS' : 'Pack Complet';
      const optionsText = options.length > 0 ? ` + ${options.join(' + ')}` : '';
      const totalPrice = getTotalPrice();
      const discountedPrice = getDiscountedPrice();
      const activeDiscount = getActiveDiscount();
      const finalPrice = activeDiscount > 0 ? discountedPrice : totalPrice;
      
      // Get affiliate code from localStorage (from streamer link)
      const affiliateCode = localStorage.getItem('affiliate_code');
      // Manual referral code (user referral for points)
      const userReferralCode = referralCode || null;
      
      console.log('💳 PAYPAL/PSC - Creating ticket with affiliate:', affiliateCode, ', referral:', userReferralCode, ', promo:', promoValidated ? promoCode : null);

      const response = await fetch(`${API_URL}/api/create-payment-ticket`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          payment_method: method,
          service_type: selectedService,
          service_name: `${serviceName}${optionsText}`,
          total_price: finalPrice,
          options: {
            priority: isPriorityChecked,
            preparation: isPreparationChecked,
            intel: isIntelChecked && (selectedService === 'pack' || selectedService === 'bios')
          },
          referral_code: userReferralCode,
          affiliate_code: affiliateCode,
          promo_code: promoValidated ? promoCode.toUpperCase() : null,
          original_price: (promoValidated && activeDiscount > 0) ? totalPrice : null,
          discount_percent: (promoValidated && activeDiscount > 0) ? activeDiscount : null
        })
      });

      if (!response.ok) {
        let error = {};
        try {
          error = await response.json();
        } catch (_) {
          error = { detail: `Erreur ${response.status}${response.statusText ? ' — ' + response.statusText : ''}` };
        }
        if (response.status === 403 && (error.detail === 'not_in_discord' || error.detail === 'discord_banned')) {
          setShowModal(false);
          setAltPaymentLoading(null);
          showDiscordRequired(error.detail);
          return;
        }
        if (response.status === 409) {
          const detail = error.detail || '';
          const urlMatch = detail.match(/https?:\/\/[^\s]+/);
          setShowModal(false);
          setConflict({
            type: detail.toLowerCase().includes('ticket') ? 'ticket' : 'order',
            method,
            message: detail,
            discord_url: urlMatch ? urlMatch[0] : null,
            serviceName: `${serviceName}${optionsText}`,
          });
          return;
        }
        throw new Error(error.detail || `Erreur ${response.status}`);
      }

      let data;
      try {
        data = await response.json();
      } catch (_) {
        throw new Error('Réponse invalide du serveur');
      }
      
      // Close modal and show success popup
      setShowModal(false);
      setPaypalTicketData({
        method: method,
        discord_url: data.discord_url,
        serviceName: `${serviceName}${optionsText}`,
        totalPrice: method === 'paysafecard' ? totalPrice + 5 : totalPrice
      });
      setShowPaypalSuccess(true);

    } catch (err) {
      console.error('Error:', err);
      setOrderError(err.message || 'Une erreur est survenue');
    } finally {
      setAltPaymentLoading(null);
    }
  };

  // Prix - Preparation is always +5€, Intel surcharge is +3.99€
  const PREPARATION_PRICE = 5;
  const INTEL_PRICE = 3.99;

  // Features from translations
  const windowsFeatures = t('services.windows.features') || [
    "Installation Windows AqmOS",
    "Optimisation Complète du Windows",
    "Optimisation du registre Windows",
    "Installation et optimisation des pilotes requis.",
    "Optimisation des logiciels requis (Discord, Spotify, OBS, steam etc.).",
    "Désactivation total des économies d'énergie",
    "Plan d'alimentation custom",
    "GameMode (reduction de la latence)",
    "Optimisation du stockage (SSD,HDD)",
    "Et bien plus encore .."
  ];

  const packFeatures = t('services.pack.features') || [
    "Tout le Service Windows +",
    "Optimisation complète du BIOS",
    "Configuration RAM (XMP/EXPO)",
    "Désactivation des paramètres inutiles",
    "Above 4G / Resize BAR",
    "Meilleur Gain FPS"
  ];

  // Texte dynamique
  const serviceIncludes = language === 'EN' ? 'Service includes:' : 'Le service comprend :';

  // Prix
  const windowsStandardBase = srvPrices?.windows ?? 19;
  const windowsPriorityExtra = 4;
  const packStandardBase = packEffectiveBase;
  const packPriorityExtra = isClient ? 4 : 8;
  const biosStandardBase = srvPrices?.bios ?? 13.99;
  const biosPriorityExtra = 4;

  const getBasePrice = () => {
    if (selectedService === 'windows') return windowsStandardBase;
    if (selectedService === 'pack') return packStandardBase;
    if (selectedService === 'bios') return biosStandardBase;
    return 0;
  };

  const getPriorityExtra = () => {
    if (selectedService === 'windows') return windowsPriorityExtra;
    if (selectedService === 'pack') return packPriorityExtra;
    if (selectedService === 'bios') return biosPriorityExtra;
    return 0;
  };

  // Get the best discount (flash sale, promo code, or referral)
  const getActiveDiscount = () => {
    const flashDiscount = getFlashSaleDiscount(selectedService);
    const promoDiscountValue = promoValidated ? promoDiscount : 0;
    return Math.max(flashDiscount, promoDiscountValue, referralDiscountFor(selectedService));
  };

  const getTotalPrice = () => {
    let total = getBasePrice();
    if (isPriorityChecked) total += getPriorityExtra();
    if (isPreparationChecked) total += PREPARATION_PRICE;
    if (isIntelChecked && (selectedService === 'pack' || selectedService === 'bios')) total += INTEL_PRICE;
    return Math.round(total * 100) / 100;
  };

  const getDiscountedPrice = () => {
    const total = getTotalPrice();
    const discount = getActiveDiscount();
    if (discount > 0) {
      return Math.round(total * (1 - discount / 100) * 100) / 100;
    }
    return total;
  };

  return (
    <div className="min-h-screen py-20 px-4" data-testid="services-page">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-block px-3 py-1 text-xs font-medium text-cyan-400 bg-cyan-500/10 rounded-full border border-cyan-500/20 mb-4 animate-pulse">
            +{stats.optimizedPCs} {t('services.badge')}
          </span>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
            {t('services.title')} <span className="text-cyan-400">{t('services.titleHighlight')}</span>
          </h1>
          <p className="text-gray-400 text-sm max-w-md mx-auto">
            {t('services.subtitle')}
          </p>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-6 p-3 bg-green-500/10 border border-green-500/40 rounded-lg text-green-400 text-center text-sm font-medium" data-testid="success-message">
            ✓ {t('services.successMessage')}
          </div>
        )}

        {/* Error Message */}
        {orderError && (
          <div className="mb-6 p-4 bg-orange-500/10 border border-orange-500/40 rounded-lg text-orange-400 text-center text-sm font-medium flex items-center justify-center gap-2" data-testid="error-message">
            <AlertCircle className="w-5 h-5" />
            {orderError}
          </div>
        )}

        {/* Active Order Notice */}
        {hasActiveOrder && !success && (
          <div className="mb-6 p-4 bg-cyan-500/10 border border-cyan-500/40 rounded-lg text-cyan-400 text-center text-sm" data-testid="active-order-notice">
            <span className="font-medium">📋 {t('services.activeOrderNotice')}</span>
            <span className="text-gray-400 ml-1">{t('services.activeOrderHint')}</span>
          </div>
        )}

        {/* ── LICENCE CARDS ───────────────────────────────────────────────── */}
        {activeCategory === 'licence' && (
          <div data-testid="licence-section">
            {/* Info banner */}
            <div className="mb-6 flex items-start gap-3 px-4 py-3 bg-indigo-500/10 border border-indigo-500/20 rounded-xl text-sm text-indigo-200">
              <Key className="w-4 h-4 flex-shrink-0 mt-0.5 text-indigo-400" />
              <span>
                Chaque licence s'<strong className="text-white">additionne</strong> à tes utilisations existantes.
                Récupère ta clé sur Discord avec la commande <code className="bg-white/10 px-1 rounded text-xs">/cle</code>.
              </span>
            </div>

            {/* 3-column grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {licenceTiers.map((tier) => (
                <div
                  key={tier.id}
                  data-testid={`licence-card-${tier.id}`}
                  className={`relative flex flex-col rounded-xl p-5 border transition-all duration-300 hover:-translate-y-1
                    ${tier.popular
                      ? 'bg-indigo-900/30 border-indigo-500/50 shadow-[0_0_30px_rgba(99,102,241,0.15)]'
                      : tier.lifetime
                        ? 'bg-yellow-900/20 border-yellow-500/30 shadow-[0_0_20px_rgba(234,179,8,0.08)]'
                        : 'bg-zinc-900/80 border-zinc-800 hover:border-indigo-500/40'
                    }`}
                >
                  {/* Popular badge */}
                  {tier.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-indigo-600 text-white text-[11px] font-bold rounded-full shadow-lg">
                      Populaire
                    </div>
                  )}
                  {tier.lifetime && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-yellow-500 text-black text-[11px] font-bold rounded-full shadow-lg">
                      Meilleur deal
                    </div>
                  )}

                  {/* Header */}
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                      tier.lifetime ? 'bg-yellow-500/15 border border-yellow-500/25' : 'bg-indigo-500/15 border border-indigo-500/25'
                    }`}>
                      {tier.lifetime
                        ? <Infinity className={`w-4 h-4 text-yellow-400`} />
                        : <Key className="w-4 h-4 text-indigo-400" />}
                    </div>
                    <div>
                      <p className="text-white font-bold text-base leading-none">
                        {tier.lifetime ? 'Lifetime' : `${tier.uses} utilisation${tier.uses > 1 ? 's' : ''}`}
                      </p>
                      <p className="text-gray-500 text-xs mt-0.5">AqmOpti</p>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="mb-4">
                    <span className={`text-3xl font-black ${tier.lifetime ? 'text-yellow-300' : 'text-white'}`}>
                      {tier.price.toFixed(2)}€
                    </span>
                    {!tier.lifetime && (
                      <span className="text-gray-500 text-xs ml-1">
                        · {(tier.price / tier.uses).toFixed(2)}€/utilisation
                      </span>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="space-y-1.5 mb-5 flex-1">
                    {[
                      tier.lifetime ? 'Utilisations illimitées' : `${tier.uses} activation${tier.uses > 1 ? 's' : ''} AqmOpti`,
                      'Licence liée à ton Discord',
                      'Récupère avec /cle sur Discord',
                      tier.lifetime ? 'Valable à vie' : 'Cumulable avec tes licences',
                    ].map((f) => (
                      <li key={f} className="flex items-center gap-2 text-xs text-gray-300">
                        <Check className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>

                  {/* Actions — PayPal first (favored) */}
                  <div className="space-y-2">
                    <button
                      data-testid={`licence-paypal-${tier.id}`}
                      onClick={() => handleLicenceOrder(tier.id, 'paypal')}
                      disabled={!!licenceLoading}
                      className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2
                        ${tier.popular
                          ? 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-[0_4px_14px_rgba(99,102,241,0.3)]'
                          : tier.lifetime
                            ? 'bg-yellow-500 hover:bg-yellow-400 text-black'
                            : 'bg-white/8 hover:bg-indigo-600 hover:text-white text-gray-200 border border-white/10'
                        }`}
                    >
                      Commander — PayPal
                    </button>
                    <div className="flex gap-2">
                      <button
                        data-testid={`licence-stripe-${tier.id}`}
                        onClick={() => handleLicenceOrder(tier.id, 'stripe')}
                        disabled={licenceLoading === `${tier.id}_stripe`}
                        className="flex-1 py-2 rounded-lg text-xs font-medium bg-white/8 hover:bg-white/15 text-gray-200 border border-white/10 transition-colors flex items-center justify-center gap-1.5"
                      >
                        {licenceLoading === `${tier.id}_stripe` ? (
                          <span className="animate-spin w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full" />
                        ) : (
                          <>Carte +1€</>
                        )}
                      </button>
                      <button
                        data-testid={`licence-psc-${tier.id}`}
                        onClick={() => handleLicenceOrder(tier.id, 'paysafecard')}
                        disabled={!!licenceLoading}
                        className="flex-1 py-2 rounded-lg text-xs font-medium bg-[#00b9f1]/10 hover:bg-[#00b9f1]/20 text-[#00b9f1] border border-[#00b9f1]/20 transition-colors"
                      >
                        Paysafecard +5€
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── OPTIMISATION CARDS ───────────────────────────────────────────── */}
        {activeCategory === 'optimisation' && (
          <div className="optimization-catalog" data-testid="optimization-catalog">
          <div className="optimization-offers" data-testid="optimization-main-offers">
          {isClient ? (
            <LicenceUsesCard tiers={usesCardTiers} t={t} onViewOffers={() => navigate('/licences')} />
          ) : (
          <WindowsCard
            t={t}
            isAuthenticated={isAuthenticated}
            isClient={isClient}
            loading={loading}
            success={success}
            windowsStandardBase={windowsStandardBase}
            windowsFeatures={windowsFeatures}
            getFlashSaleDiscount={getCardDiscount}
            openOrderModal={openOrderModal}
            flashSaleEnd={flashSale?.active ? flashSale.end_time : null}
          />
          )}

          <PackCard
            t={t}
            language={language}
            isAuthenticated={isAuthenticated}
            isClient={isClient}
            loading={loading}
            success={success}
            packStandardBase={packStandardBase}
            packDisc={packDisc}
            windowsStandardBase={windowsStandardBase}
            biosStandardBase={biosStandardBase}
            getFlashSaleDiscount={getCardDiscount}
            openOrderModal={openOrderModal}
            flashSaleEnd={flashSale?.active ? flashSale.end_time : null}
          />
          </div>
          <section className="optimization-unit-services" aria-labelledby="unit-services-title" data-testid="unit-services-section">
            <div className="service-section-heading">
              <h2 id="unit-services-title" data-testid="unit-services-title">{language === 'EN' ? 'Individual services' : 'Services unitaires'}</h2>
            </div>
            <div className="optimization-offers">
              <BiosCard t={t} isClient={isClient} isAuthenticated={isAuthenticated} biosStandardBase={biosStandardBase}
                loading={loading} biosDisc={biosDisc} openOrderModal={openOrderModal} loginWithDiscord={loginWithDiscord} />
            </div>
          </section>

          </div>
        )} {/* end activeCategory === 'optimisation' */}

        {/* Advantages Section */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-8 md:mb-10">
          <div className="flex items-center gap-2 md:gap-3 p-3 md:p-4 bg-zinc-900/50 rounded-xl border border-zinc-800">
            <Shield className="w-5 h-5 md:w-6 md:h-6 text-cyan-400 flex-shrink-0" />
            <div>
              <p className="text-white text-xs md:text-sm font-medium">{t('services.cards.securePayment')}</p>
              <p className="text-gray-500 text-[10px] md:text-xs">CB • Stripe • PayPal • Paysafecard</p>
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-3 p-3 md:p-4 bg-zinc-900/50 rounded-xl border border-zinc-800">
            <Clock className="w-5 h-5 md:w-6 md:h-6 text-cyan-400 flex-shrink-0" />
            <div>
              <p className="text-white text-xs md:text-sm font-medium">{t('services.cards.instantOptimize')}</p>
              <p className="text-gray-500 text-[10px] md:text-xs">{t('services.cards.instantOptimizeSub')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-3 p-3 md:p-4 bg-zinc-900/50 rounded-xl border border-zinc-800">
            <Headphones className="w-5 h-5 md:w-6 md:h-6 text-cyan-400 flex-shrink-0" />
            <div>
              <p className="text-white text-xs md:text-sm font-medium">{t('services.cards.support247')}</p>
              <p className="text-gray-500 text-[10px] md:text-xs">{t('services.cards.supportSub')}</p>
            </div>
          </div>
        </div>

        {/* Priority Selection Modal */}
        {showModal && (
          <div data-testid="service-order-modal" className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-end md:items-center justify-center z-50 p-0 md:p-4" onClick={() => setShowModal(false)}>
            <div className="bg-[#0f0f10] border border-zinc-800 rounded-t-2xl md:rounded-xl w-full md:max-w-md max-h-[95vh] md:max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
              
              {/* Modal Header */}
              <div className="bg-[#0f0f10] p-4 md:p-5 border-b border-zinc-800/50">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-white font-semibold text-base md:text-lg">
                      {language === 'EN' ? 'Complete your order' : 'Finaliser la commande'}
                    </h3>
                    <p className="text-gray-500 text-xs md:text-sm mt-0.5">
                      {selectedService === 'windows' 
                        ? (language === 'EN' ? 'Windows Service' : 'Service Windows')
                        : selectedService === 'bios'
                        ? (language === 'EN' ? 'BIOS Optimization' : 'Optimisation BIOS')
                        : (language === 'EN' ? 'Full Pack' : 'Pack Complet')}
                    </p>
                  </div>
                  <button 
                    onClick={() => setShowModal(false)} data-testid="service-order-close-button" aria-label="Fermer"
                    className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-3 md:p-5 space-y-2.5 md:space-y-3">
                
                {/* Prix de base */}
                <div className="p-2.5 md:p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl">
                  <div className="flex items-center justify-between">
                    <p className="text-white font-medium text-xs md:text-sm">
                      {language === 'EN' ? 'Base price' : 'Prix de base'}
                    </p>
                    <span data-testid="service-order-base-price" className="text-lg md:text-2xl font-bold text-cyan-400">
                      {getBasePrice()}€
                    </span>
                  </div>
                </div>

                {/* Option Prioritaire — pack complet + BIOS */}
                {(selectedService === 'pack' || selectedService === 'bios') && (
                <button
                  type="button"
                  onClick={() => setIsPriorityChecked(!isPriorityChecked)} data-testid="service-priority-checkbox" aria-pressed={isPriorityChecked}
                  className={`w-full p-2.5 md:p-4 rounded-xl border transition-[background-color,border-color,color] duration-200 ease-out active:scale-[0.99] text-left ${
                    isPriorityChecked 
                      ? 'bg-orange-950/30 border-orange-600/60' 
                      : 'bg-zinc-900/30 border-zinc-800 hover:border-zinc-700'
                  }`}
                >
                  <div className="flex items-center gap-2.5 md:gap-3">
                    <div className={`flex-shrink-0 w-4 h-4 md:w-5 md:h-5 rounded border-2 flex items-center justify-center transition-colors duration-150 ${
                      isPriorityChecked ? 'bg-orange-500 border-orange-500' : 'border-zinc-600'
                    }`}>
                      <Check className={`w-2.5 h-2.5 md:w-3 md:h-3 text-white transition-all duration-200 ease-out ${isPriorityChecked ? 'scale-100 opacity-100' : 'scale-50 opacity-0'}`} strokeWidth={3} />
                    </div>
                    <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className={`font-semibold text-xs md:text-sm truncate ${isPriorityChecked ? 'text-orange-400' : 'text-white'}`}>
                          {language === 'EN' ? 'Priority Option' : 'Option Prioritaire'}
                        </p>
                        <p className="text-gray-500 text-[10px] md:text-xs leading-tight mt-0.5">{language === 'EN' ? 'Your order comes first' : 'Votre commande passe en priorité'}</p>
                      </div>
                      <span className={`text-sm md:text-base font-bold whitespace-nowrap ${isPriorityChecked ? 'text-orange-400' : 'text-gray-400'}`}>
                        +{getPriorityExtra()}€
                      </span>
                    </div>
                  </div>
                  {(selectedService === 'bios' || selectedService === 'pack') && (
                    <p className={`text-[10px] md:text-xs mt-1.5 ml-7 md:ml-8 leading-tight flex items-center gap-1 ${isPriorityChecked ? 'text-orange-300/90' : 'text-gray-600'}`}>
                      <Clock className="w-3 h-3 flex-shrink-0" />
                      {isPriorityChecked
                        ? (language === 'EN' ? 'BIOS optimization instantly after payment' : 'Optimisation BIOS instantanément après le paiement')
                        : (language === 'EN' ? 'BIOS optimization done within 24h' : 'Optimisation BIOS réalisée sous 24h')}
                    </p>
                  )}
                </button>
                )}

                {/* Option Préparation — not available for BIOS-only service */}
                {selectedService !== 'bios' && (
                <button
                  type="button"
                  onClick={() => setIsPreparationChecked(!isPreparationChecked)} data-testid="service-preparation-checkbox" aria-pressed={isPreparationChecked}
                  className={`w-full p-2.5 md:p-4 rounded-xl border transition-[background-color,border-color,color] duration-200 ease-out active:scale-[0.99] text-left ${
                    isPreparationChecked 
                      ? 'bg-cyan-950/30 border-cyan-600/60' 
                      : 'bg-zinc-900/30 border-zinc-800 hover:border-zinc-700'
                  }`}
                >
                  <div className="flex items-center gap-2.5 md:gap-3">
                    <div className={`flex-shrink-0 w-4 h-4 md:w-5 md:h-5 rounded border-2 flex items-center justify-center transition-colors duration-150 ${
                      isPreparationChecked ? 'bg-cyan-500 border-cyan-500' : 'border-zinc-600'
                    }`}>
                      <Check className={`w-2.5 h-2.5 md:w-3 md:h-3 text-white transition-all duration-200 ease-out ${isPreparationChecked ? 'scale-100 opacity-100' : 'scale-50 opacity-0'}`} strokeWidth={3} />
                    </div>
                    <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className={`font-semibold text-xs md:text-sm truncate ${isPreparationChecked ? 'text-cyan-400' : 'text-white'}`}>
                          {language === 'EN' ? 'Preparation Option' : 'Option Préparation'}
                        </p>
                        <p className="text-gray-500 text-[10px] md:text-xs leading-tight mt-0.5">{language === 'EN' ? 'We handle everything for you: Windows reinstall + all our tutorials done on your behalf' : "On s'occupe de tout à votre place : réinstallation Windows + tous nos tutos faits pour vous"}</p>
                      </div>
                      <span className={`text-sm md:text-base font-bold whitespace-nowrap ${isPreparationChecked ? 'text-cyan-400' : 'text-gray-400'}`}>
                        +{PREPARATION_PRICE}€
                      </span>
                    </div>
                  </div>
                </button>
                )}

                {/* Option Processeur Intel — only for Pack Complet (BIOS) */}
                {(selectedService === 'pack' || selectedService === 'bios') && (
                  <button
                    type="button"
                    onClick={() => setIsIntelChecked(!isIntelChecked)}
                    className={`w-full p-2.5 md:p-4 rounded-xl border transition-[background-color,border-color,color] duration-200 ease-out active:scale-[0.99] text-left animate-fadeIn ${
                      isIntelChecked
                        ? 'bg-blue-950/30 border-blue-600/60'
                        : 'bg-zinc-900/30 border-zinc-800 hover:border-zinc-700'
                    }`}
                    data-testid="intel-checkbox"
                  >
                    <div className="flex items-center gap-2.5 md:gap-3">
                      <div className={`flex-shrink-0 w-4 h-4 md:w-5 md:h-5 rounded border-2 flex items-center justify-center transition-colors duration-150 ${
                        isIntelChecked ? 'bg-blue-500 border-blue-500' : 'border-zinc-600'
                      }`}>
                        <Check className={`w-2.5 h-2.5 md:w-3 md:h-3 text-white transition-all duration-200 ease-out ${isIntelChecked ? 'scale-100 opacity-100' : 'scale-50 opacity-0'}`} strokeWidth={3} />
                      </div>
                      <p className={`flex-1 text-xs md:text-sm font-semibold ${isIntelChecked ? 'text-blue-400' : 'text-white'}`}>
                        J'ai un processeur Intel
                      </p>
                      <span className={`text-sm md:text-base font-bold whitespace-nowrap ${isIntelChecked ? 'text-blue-400' : 'text-gray-400'}`}>
                        +{INTEL_PRICE.toFixed(2)}€
                      </span>
                    </div>
                  </button>
                )}

                {/* BIOS Compatibility Warning — required for bios/pack */}
                {requiresBiosWarning() && (
                  <div
                    className={`p-2 md:p-3 rounded-xl border transition-[background-color,border-color] duration-200 ease-out animate-fadeIn ${
                      biosCompatibilityChecked
                        ? 'bg-green-950/20 border-green-600/50'
                        : 'bg-red-950/20 border-red-600/40'
                    }`}
                    data-testid="bios-compatibility-section"
                  >
                    <div className="flex items-start gap-1.5 md:gap-2 mb-1.5 md:mb-2">
                      <AlertCircle className={`w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0 mt-0.5 transition-colors duration-200 ${biosCompatibilityChecked ? 'text-green-400' : 'text-red-400'}`} />
                      <div className="flex-1 min-w-0">
                        <p className={`font-semibold text-[11px] md:text-xs transition-colors duration-200 ${biosCompatibilityChecked ? 'text-green-400' : 'text-red-400'}`}>
                          {t('services.cards.biosCompatTitle')}
                        </p>
                        <p className="text-gray-400 text-[10px] md:text-[11px] mt-0.5 leading-snug">
                          <span className="text-red-400">✕</span> {t('services.cards.biosNotCompatible')} <span className="text-gray-300">{t('services.cards.biosNotCompatibleList')}</span>
                        </p>
                        <p className="text-gray-400 text-[10px] md:text-[11px] mt-0.5 leading-snug">
                          <span className="text-green-400">✓</span> {t('services.cards.biosCompatible')} <span className="text-gray-300">{t('services.cards.biosCompatibleList')}</span>
                        </p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => setBiosCompatibilityChecked(!biosCompatibilityChecked)}
                      className={`w-full p-1.5 md:p-2 rounded-lg border transition-[background-color,border-color] duration-200 ease-out active:scale-[0.99] text-left ${
                        biosCompatibilityChecked
                          ? 'bg-green-900/30 border-green-500/60'
                          : 'bg-zinc-900/40 border-zinc-700 hover:border-red-500/40'
                      }`}
                      data-testid="bios-compatibility-checkbox"
                    >
                      <div className="flex items-center gap-2">
                        <div className={`flex-shrink-0 w-3.5 h-3.5 md:w-4 md:h-4 rounded border-2 flex items-center justify-center transition-colors duration-150 ${
                          biosCompatibilityChecked ? 'bg-green-500 border-green-500' : 'border-zinc-500'
                        }`}>
                          <Check className={`w-2 h-2 md:w-2.5 md:h-2.5 text-white transition-all duration-200 ease-out ${biosCompatibilityChecked ? 'scale-100 opacity-100' : 'scale-50 opacity-0'}`} strokeWidth={3} />
                        </div>
                        <p className={`text-[10px] md:text-xs font-medium leading-snug transition-colors duration-200 ${biosCompatibilityChecked ? 'text-green-300' : 'text-white'}`}>
                          Je confirme que mon PC est compatible (PC fixe assemblé)
                        </p>
                      </div>
                    </button>
                  </div>
                )}

                {/* Codes (Parrainage + Promo) — collapsible to save space */}
                <button
                  type="button"
                  onClick={() => setShowCodes(!showCodes)}
                  className="w-full flex items-center justify-between p-2.5 md:p-3 bg-zinc-900/30 hover:bg-zinc-900/60 border border-zinc-800 hover:border-zinc-700 rounded-xl transition-[background-color,border-color] duration-200 ease-out active:scale-[0.99]"
                  data-testid="toggle-codes-btn"
                >
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-gray-400" />
                    <span className="text-xs md:text-sm text-gray-300">
                      J'ai un code promo
                    </span>
                    {(referralCode || promoValidated) && (
                      <span className="text-[10px] bg-cyan-500/15 text-cyan-300 px-1.5 py-0.5 rounded-full font-medium animate-fadeIn">
                        {promoValidated ? `-${promoDiscount}%` : 'actif'}
                      </span>
                    )}
                  </div>
                  <ChevronRight
                    className={`w-4 h-4 text-gray-500 transition-transform duration-300 ease-out ${showCodes ? 'rotate-90' : ''}`}
                  />
                </button>

                <div
                  className={`grid transition-[grid-template-rows,opacity,margin] duration-300 ease-out overflow-hidden ${
                    showCodes ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0 -mt-3'
                  }`}
                >
                  <div className="min-h-0">
                    <div className={`space-y-3 transition-transform duration-300 ease-out ${showCodes ? 'translate-y-0' : '-translate-y-2'}`}>
                {/* Code de Parrainage retiré — le parrainage passe désormais par le lien d'invitation Discord */}

                {/* Code Promo */}
                <div className={`p-3 md:p-4 rounded-xl border ${promoValidated ? 'bg-orange-950/30 border-orange-600/50' : 'bg-zinc-900/30 border-zinc-800'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Tag className="w-4 h-4 text-orange-400" />
                    <span className="text-white text-sm font-medium">
                      {language === 'EN' ? 'Promo code' : 'Code promo'}
                    </span>
                    <span className="text-gray-500 text-xs">
                      ({language === 'EN' ? 'optional' : 'optionnel'})
                    </span>
                    {promoValidated && (
                      <span className="ml-auto text-xs bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded-full font-semibold">
                        -{promoDiscount}%
                      </span>
                    )}
                    {!promoValidated && (
                      <div className="group relative ml-auto">
                        <Info className="w-4 h-4 text-gray-500 cursor-help" />
                        <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-xs text-gray-300 w-52 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 shadow-xl">
                          <p className="font-medium text-orange-400 mb-1">
                            {language === 'EN' ? 'Got a promo code?' : 'Vous avez un code promo ?'}
                          </p>
                          <p>
                            {language === 'EN' 
                              ? 'Enter it here to get a discount on your order.'
                              : 'Entrez-le ici pour bénéficier d\'une réduction sur votre commande.'}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={promoCode}
                      onChange={(e) => {
                        setPromoCode(e.target.value.toUpperCase());
                        setPromoValidated(false);
                        setPromoAuto(false);
                        setPromoError('');
                      }}
                      placeholder="PROMO20"
                      maxLength={20}
                      className={`flex-1 px-3 py-2.5 bg-zinc-950/50 border rounded-lg text-white font-mono text-sm focus:outline-none ${
                        promoValidated ? 'border-orange-600' : promoError ? 'border-red-600' : 'border-zinc-700 focus:border-orange-600'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={validatePromoCode}
                      disabled={promoLoading || !promoCode.trim() || promoValidated}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        promoValidated 
                          ? 'bg-orange-600 text-white' 
                          : 'bg-zinc-700 hover:bg-zinc-600 text-white disabled:opacity-50'
                      }`}
                    >
                      {promoLoading ? (
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      ) : promoValidated ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        language === 'EN' ? 'Apply' : 'Appliquer'
                      )}
                    </button>
                  </div>
                  {promoError && (
                    <p className="text-red-400 text-xs mt-2">{promoError}</p>
                  )}
                  {promoAuto && promoValidated && (
                    <p className="text-green-400 text-xs mt-2 flex items-center gap-1" data-testid="affiliate-promo-auto">
                      <Check className="w-3 h-3" />
                      {language === 'EN'
                        ? `Discount applied automatically via your affiliate link (-${promoDiscount}%)`
                        : `Réduction appliquée automatiquement via ton lien affilié (-${promoDiscount}%)`}
                    </p>
                  )}
                  {getFlashSaleDiscount(selectedService) > 0 && !promoValidated && (
                    <p className="text-orange-400 text-xs mt-2 flex items-center gap-1">
                      <Zap className="w-3 h-3" />
                      {language === 'EN' ? 'Flash sale' : 'Vente flash'} -{getFlashSaleDiscount(selectedService)}% {language === 'EN' ? 'already applied!' : 'déjà appliquée !'}
                    </p>
                  )}
                </div>
                    </div>
                  </div>
                </div>

                {/* Prix Total */}
                <div className={`p-4 rounded-xl border-2 ${
                  getActiveDiscount() > 0
                    ? 'bg-gradient-to-r from-orange-950/40 to-red-950/20 border-orange-600/50' 
                    : isPriorityChecked || isPreparationChecked
                    ? 'bg-gradient-to-r from-orange-950/30 to-cyan-950/20 border-orange-800/40' 
                    : 'bg-cyan-950/20 border-cyan-800/40'
                }`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm font-medium">
                        {language === 'EN' ? 'Total Price' : 'Prix Total'}
                      </p>
                      <p className={`font-medium text-xs mt-0.5 ${getActiveDiscount() > 0 ? 'text-orange-400' : isPriorityChecked || isPreparationChecked ? 'text-orange-400' : 'text-cyan-400'}`}>
                        {(() => {
                          const std = 'Standard';
                          const prio = language === 'EN' ? 'Priority' : 'Prioritaire';
                          const prep = language === 'EN' ? 'Preparation' : 'Préparation';
                          const options = [std];
                          if (isPriorityChecked) options.push(prio);
                          if (isPreparationChecked) options.push(prep);
                          if (isIntelChecked && (selectedService === 'pack' || selectedService === 'bios')) options.push('Intel');
                          if (getActiveDiscount() > 0) options.push(`-${getActiveDiscount()}%`);
                          return options.join(' + ');
                        })()}
                      </p>
                    </div>
                    <div className="text-right">
                      {getActiveDiscount() > 0 ? (
                        <>
                          <span className="text-2xl md:text-3xl font-bold text-orange-400">
                            {getDiscountedPrice()}€
                          </span>
                          <span className="text-sm text-gray-500 line-through ml-2">{getTotalPrice()}€</span>
                        </>
                      ) : (
                        <span className="text-2xl md:text-3xl font-bold text-white">
                          {getTotalPrice()}€
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Buttons de paiement */}
                <div className="flex flex-col gap-3 pt-2 pb-4 md:pb-0">

                  {/* BIOS compatibility blocker hint */}
                  {requiresBiosWarning() && !biosCompatibilityChecked && (
                    <p className="text-center text-xs text-red-400 -mb-1" data-testid="bios-blocker-hint">
                      ⚠️ Coche la case de compatibilité BIOS ci-dessus pour pouvoir commander
                    </p>
                  )}
                  
                  {/* PayPal - RECOMMENDED */}
                  <div className="relative">
                    <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 z-10">
                      <span className="px-3 py-1 bg-gradient-to-r from-[#0070ba] to-[#003087] text-white text-[10px] font-bold rounded-full shadow-lg">
                        ⭐ RECOMMANDÉ
                      </span>
                    </div>
                    <button
                      onClick={() => handleAlternativePayment('paypal')}
                      disabled={altPaymentLoading || (requiresBiosWarning() && !biosCompatibilityChecked)}
                      className="w-full p-4 pt-5 bg-gradient-to-r from-[#0070ba] to-[#003087] hover:from-[#005ea6] hover:to-[#00266d] text-white font-bold text-base rounded-xl transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 shadow-lg shadow-[#0070ba]/30 hover:shadow-[#0070ba]/50 hover:scale-[1.02]"
                    >
                      {altPaymentLoading === 'paypal' ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      ) : (
                        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.59 3.025-2.566 6.082-8.558 6.082H9.825c-1.028 0-1.903.748-2.062 1.762L6.439 23.89c-.08.512.312.972.832.972h4.254c.896 0 1.662-.655 1.8-1.54l.074-.39.73-4.616.047-.252c.138-.885.903-1.54 1.8-1.54h1.133c3.676 0 6.553-1.493 7.394-5.816.35-1.8.169-3.304-.73-4.362a3.352 3.352 0 0 0-.551-.429z"/>
                        </svg>
                      )}
                      {language === 'EN' ? 'Pay with PayPal' : 'Payer avec PayPal'} - {getActiveDiscount() > 0 ? getDiscountedPrice() : getTotalPrice()}€
                    </button>
                  </div>

                  {/* Autres moyens de paiement */}
                  <div className="flex gap-2">
                    {/* Carte */}
                    <button
                      onClick={handleOrder}
                      disabled={loading || (requiresBiosWarning() && !biosCompatibilityChecked)}
                      className="flex-1 p-3 bg-zinc-800/60 hover:bg-zinc-700 border border-zinc-700 text-white font-medium text-sm rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <div className="w-4 h-4 border-2 border-gray-500/30 border-t-gray-400 rounded-full animate-spin"></div>
                      ) : (
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                          <line x1="1" y1="10" x2="23" y2="10"></line>
                        </svg>
                      )}
                      {language === 'EN' ? 'Card' : 'Carte'}
                      {selectedService === 'windows' ? ' +1€' : selectedService === 'pack' ? ' +2,50€' : ''}
                    </button>
                    
                    {/* Paysafecard */}
                    <button
                      onClick={() => handleAlternativePayment('paysafecard')}
                      disabled={altPaymentLoading || (requiresBiosWarning() && !biosCompatibilityChecked)}
                      className="flex-1 p-3 bg-zinc-800/60 hover:bg-zinc-700 border border-zinc-700 text-white font-medium text-sm rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {altPaymentLoading === 'paysafecard' ? (
                        <div className="w-4 h-4 border-2 border-gray-500/30 border-t-gray-400 rounded-full animate-spin"></div>
                      ) : (
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                        </svg>
                      )}
                      Paysafecard +5€
                    </button>
                  </div>
                  
                  <button
                    onClick={() => setShowModal(false)}
                    className="w-full p-2 text-gray-500 hover:text-gray-400 font-medium text-sm transition-all"
                  >
                    {language === 'EN' ? 'Cancel' : 'Annuler'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PayPal Success Popup */}
        {showPaypalSuccess && paypalTicketData && (
          <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowPaypalSuccess(false)}>
            <div
              className="bg-[#0e1120] rounded-2xl w-full max-w-md border border-white/8 shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-6 pt-8 pb-6 text-center">
                <div className="w-14 h-14 bg-green-500/15 border border-green-500/25 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-7 h-7 text-green-400" strokeWidth={2.5} />
                </div>
                <h3 className="text-xl font-bold text-white mb-1">{t('services.cards.ticketCreatedTitle')}</h3>
                <p className="text-gray-500 text-sm">
                  {t('services.cards.paymentVia')} <span className="text-gray-300 font-medium">{paypalTicketData.method === 'paypal' ? 'PayPal' : 'Paysafecard'}</span>
                </p>
              </div>

              {/* Divider */}
              <div className="h-px bg-white/5 mx-6" />

              {/* Content */}
              <div className="px-6 py-5 space-y-4">

                {/* Order recap */}
                <div className="flex items-center justify-between py-3 px-4 bg-white/4 rounded-xl border border-white/6">
                  <div className="min-w-0 mr-3">
                    <p className="text-[11px] text-gray-500 mb-0.5 uppercase tracking-wide">{t('services.cards.serviceLabel')}</p>
                    <p className="text-white text-sm font-medium leading-snug">{paypalTicketData.serviceName}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-[11px] text-gray-500 mb-0.5 uppercase tracking-wide">{t('services.total')}</p>
                    <p className="text-white font-bold text-base">{paypalTicketData.totalPrice}€</p>
                  </div>
                </div>

                {/* Steps */}
                <div className="space-y-2">
                  {paypalTicketData.method === 'paypal' ? (
                    <>
                      {[
                        { n: 1, text: <span>{language === 'EN' ? <>Join your <strong className="text-white">Discord ticket</strong></> : <>Rejoins ton <strong className="text-white">ticket Discord</strong></>}</span> },
                        { n: 2, text: <span>{language === 'EN' ? <>Click the <strong className="text-[#0070ba]">PayPal</strong> button to see the address</> : <>Clique sur le bouton <strong className="text-[#0070ba]">PayPal</strong> pour voir l'adresse</>}</span> },
                        { n: 3, text: <span>{language === 'EN' ? <>Send <strong className="text-white">{paypalTicketData.totalPrice}€</strong> in <strong className="text-yellow-400">"Friends &amp; Family"</strong> mode</> : <>Envoie <strong className="text-white">{paypalTicketData.totalPrice}€</strong> en mode <strong className="text-yellow-400">"Entre proches"</strong></>}</span> },
                        { n: 4, text: <span>{language === 'EN' ? <>Share the <strong className="text-white">screenshot</strong> in the ticket</> : <>Partage le <strong className="text-white">screenshot</strong> dans le ticket</>}</span> },
                      ].map(({ n, text }) => (
                        <div key={n} className="flex items-start gap-3">
                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-500/20 border border-indigo-500/30 text-indigo-300 text-[10px] font-bold flex items-center justify-center mt-0.5">{n}</span>
                          <span className="text-gray-400 text-sm leading-snug">{text}</span>
                        </div>
                      ))}
                    </>
                  ) : (
                    <>
                      {[
                        { n: 1, text: <span>{language === 'EN' ? <>Buy a <strong className="text-[#00b9f1]">Paysafecard</strong> worth <strong className="text-white">{paypalTicketData.totalPrice}€</strong></> : <>Achète une carte <strong className="text-[#00b9f1]">Paysafecard</strong> de <strong className="text-white">{paypalTicketData.totalPrice}€</strong></>}</span> },
                        { n: 2, text: <span>{language === 'EN' ? <>Join your <strong className="text-white">Discord ticket</strong></> : <>Rejoins ton <strong className="text-white">ticket Discord</strong></>}</span> },
                        { n: 3, text: <span>{language === 'EN' ? <>Send the <strong className="text-yellow-400">16-digit code</strong> in the ticket</> : <>Envoie le <strong className="text-yellow-400">code à 16 chiffres</strong> dans le ticket</>}</span> },
                        { n: 4, text: <span>{language === 'EN' ? <>An admin will <strong className="text-white">validate</strong> your order</> : <>Un admin <strong className="text-white">validera</strong> ta commande</>}</span> },
                      ].map(({ n, text }) => (
                        <div key={n} className="flex items-start gap-3">
                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-500/20 border border-indigo-500/30 text-indigo-300 text-[10px] font-bold flex items-center justify-center mt-0.5">{n}</span>
                          <span className="text-gray-400 text-sm leading-snug">{text}</span>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="px-6 pb-6 space-y-2.5">
                <button
                  onClick={() => {
                    if (paypalTicketData.discord_url) {
                      window.open(paypalTicketData.discord_url, '_blank');
                    }
                  }}
                  className="w-full py-3 bg-[#5865F2] hover:bg-[#4752C4] text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <MessageCircle className="w-4 h-4" />
                  {language === 'EN' ? 'Join Discord' : 'Rejoindre Discord'}
                  <ExternalLink className="w-3.5 h-3.5 opacity-70" />
                </button>
                <button
                  onClick={() => setShowPaypalSuccess(false)}
                  className="w-full py-2.5 text-gray-500 hover:text-gray-300 font-medium rounded-xl transition-colors text-sm"
                >
                  {language === 'EN' ? 'Close' : 'Fermer'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Conflict popup (already has ticket / order) */}
        {conflict && (
          <div
            className="fixed inset-0 z-[110] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
            onClick={() => setConflict(null)}
            data-testid="services-conflict-modal"
          >
            <div
              className="w-full max-w-md bg-zinc-900 border border-orange-500/40 rounded-2xl shadow-[0_0_60px_-12px_rgba(249,115,22,0.4)] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 text-center border-b border-zinc-800">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-500/15 border border-orange-500/40 flex items-center justify-center">
                  <AlertCircle className="w-9 h-9 text-orange-400" />
                </div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-orange-400 mb-1.5 font-semibold">
                  {conflict.type === 'ticket' ? (language === 'EN' ? 'Ticket already open' : 'Ticket déjà ouvert') : (language === 'EN' ? 'Active order' : 'Commande en cours')}
                </p>
                <h3 className="text-xl font-bold text-white mb-2">
                  {conflict.type === 'ticket'
                    ? (language === 'EN' ? 'You already have an open ticket' : 'Tu as déjà un ticket en cours')
                    : (language === 'EN' ? 'An order is already active' : 'Une commande est déjà active')}
                </h3>
                <p className="text-sm text-gray-400 leading-relaxed">
                  {conflict.type === 'ticket' ? (
                    language === 'EN' ? (
                      <>
                        You already opened a ticket for <span className="text-white font-semibold">{conflict.serviceName}</span>.
                        To avoid duplicates, finish it first or contact the staff.
                      </>
                    ) : (
                      <>
                        Tu as déjà ouvert un ticket pour <span className="text-white font-semibold">{conflict.serviceName}</span>.
                        Pour éviter les doublons, finalise-le d'abord ou contacte le staff.
                      </>
                    )
                  ) : (
                    language === 'EN' ? (
                      <>
                        You already have an active <span className="text-white font-semibold">{conflict.serviceName}</span> order.
                        Wait for the staff to finish it before starting a new one.
                      </>
                    ) : (
                      <>
                        Tu as déjà une commande <span className="text-white font-semibold">{conflict.serviceName}</span> active.
                        Attends qu'elle soit terminée par le staff avant d'en lancer une nouvelle.
                      </>
                    )
                  )}
                </p>
              </div>

              <div className="p-5 space-y-3">
                {conflict.discord_url && (
                  <a
                    href={conflict.discord_url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => setConflict(null)}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#5865F2] hover:bg-[#4752C4] text-white font-semibold text-sm transition-colors"
                    data-testid="services-conflict-discord-btn"
                  >
                    <ExternalLink className="w-4 h-4" />
                    {language === 'EN' ? 'Join my existing ticket' : 'Rejoindre mon ticket existant'}
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => setConflict(null)}
                  className="w-full px-4 py-2.5 rounded-xl text-sm text-gray-400 hover:text-white hover:bg-zinc-800 transition-colors"
                  data-testid="services-conflict-close-btn"
                >
                  {language === 'EN' ? 'Got it' : "D'accord, j'ai compris"}
                </button>
              </div>

              <div className="px-5 pb-5 text-[11px] text-gray-500 text-center leading-relaxed">
                {language === 'EN' ? '💡 Need help? Contact the staff on Discord to cancel or unlock your order.' : "💡 Besoin d'aide ? Contacte le staff sur Discord pour annuler ou débloquer ta commande."}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServicesPage;
